<script setup lang="ts">
import type { EventVault } from "@/models"
import type { ToastHtmlElement } from "@/types"
import { useQueryClient } from "@tanstack/vue-query"
import { computed, ref, useTemplateRef } from "vue"
import { GET_EVENT_VAULT_QUERY_KEY, useStartEvent } from "@/api"
import { BaseModal, Button, Card, Toast } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { StartEventModalKey } from "@/symbols"

interface StartModalProps {
  isLoading?: boolean
  eventVault?: EventVault
  drawerKey?: symbol
}

const props = withDefaults(defineProps<StartModalProps>(), {
  isLoading: false,
  drawerKey: StartEventModalKey,
})

const errorToast = useTemplateRef<ToastHtmlElement | null>("errorToast")
const successToast = useTemplateRef<ToastHtmlElement | null>("successToast")
const queryClient = useQueryClient()
const { t, d } = useTranslation()

const startDateOffset = ref<number>(import.meta.env.VITE_START_DATE_OFFSET_DAYS ? Number(import.meta.env.VITE_START_DATE_OFFSET_DAYS) : 14)

const activeDate = computed<string>(() => {
  if (!props.eventVault?.eventDate)
    return ""

  const dateClone = new Date(props.eventVault.eventDate)
  const twoWeeksPrior = new Date(dateClone.setDate(dateClone.getDate() - startDateOffset.value))

  return d(twoWeeksPrior, "short")
})

const { toggle } = useModal(props.drawerKey)

const { mutate, isPending } = useStartEvent()

function onConfirm() {
  if (!props.eventVault?.id) {
    toggle()
    return
  }

  mutate(props.eventVault.id, {
    async onSuccess() {
      successToast.value?.showToast({
        title: t("start_event_modal.success_title"),
        content: t("start_event_modal.success_content"),
        icon: "CircleCheck",
      })
      await queryClient.invalidateQueries({ queryKey: [GET_EVENT_VAULT_QUERY_KEY] })
      await queryClient.invalidateQueries({ queryKey: ["listEventVaults"] })
      toggle()
    },
    onError() {
      errorToast.value?.showToast()
    },
  })
}
</script>

<template>
  <BaseModal :name="drawerKey">
    <Card>
      <template #header>
        <div class="flex items-center justify-between mb-4">
          <h1 class="font-comfortaa font-semibold text-xl">
            {{ t('start_event_modal.title') }}
          </h1>
          <Button variant="flat" prefix-icon="Close" @click="toggle">
            <span class="sr-only">{{ t('start_event_modal.close') }}</span>
          </Button>
        </div>
      </template>
      <div v-if="isLoading || isPending" class="flex flex-col gap-4">
        <div class="gpx-skeleton w-full h-24" />
        <div class="gpx-skeleton w-full h-12" />
        <div class="gpx-skeleton w-full h-24" />
        <div class="flex items-center justify-end gap-3">
          <div class="gpx-skeleton w-24 h-12" />
          <div class="gpx-skeleton w-24 h-12" />
        </div>
      </div>
      <div v-else>
        <i18n-t keypath="start_event_modal.p1" tag="p" scope="global" class="mb-8">
          <template #date>
            <span class="font-bold" data-test="start-date">{{ activeDate }}</span>
          </template>
        </i18n-t>
        <p>
          {{ t('start_event_modal.p2') }}
        </p>
        <div class="flex items-center justify-end gap-3 mt-5">
          <Button variant="tertiary" @click="toggle">
            {{ t('cancel') }}
          </Button>
          <Button @click="onConfirm">
            {{ t('confirm') }}
          </Button>
        </div>
      </div>
    </Card>
  </BaseModal>

  <Toast ref="errorToast" variant="danger" />
  <Toast ref="successToast" variant="success" />
</template>
